import { inject, Injectable } from '@angular/core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { InputFieldPropertiesInterface } from '../../interfaces/input-field-properties.interface';
import { InputFieldPropertyModel } from '../../models/low-level/input-field-property.model';
import { initInputFieldProperties } from '../../state/input-field-properties/actions/input-field-properties.actions';
import {
    inputFieldPropertiesToModels,
    inputFieldPropertyStateToModel,
} from '../../state/input-field-properties/input-field-properties.converters';
import {
    selectInputFieldProperties,
    selectInputFieldPropertiesLoaded,
    selectInputFieldPropertiesStatus,
    selectInputFieldPropertyMatch,
} from '../../state/input-field-properties/selectors/input-field-properties.selector';
import { LoadStatus } from '../../state/load-status';
import { EventService } from '../event/event.service';

/**
 * NgRx-backed implementation of {@link InputFieldPropertiesInterface}. The input-field-properties
 * reference data lives in the input-field-properties store slice; every read goes through a
 * selector instead of an in-memory array. The persistence effects mirror the slice to the
 * concurrency store, so the properties survive a restart and are available offline.
 */
@Injectable({
    providedIn: 'root',
})
export class InputFieldPropertiesNgrxService implements InputFieldPropertiesInterface {
    private readonly store = inject(Store);
    private readonly eventService = inject(EventService);
    private readonly log = inject(LogInstanceToken);

    public get isInitialized(): boolean {
        return this.store.selectSignal(selectInputFieldPropertiesLoaded)();
    }

    public constructor() {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    /**
     * Trigger the load effect and resolve once the slice settles: complete with the loaded
     * properties on success, or error on failure.
     */
    public init(): Observable<InputFieldPropertyModel[]> {
        this.store.dispatch(initInputFieldProperties());
        return this.store.select(selectInputFieldPropertiesStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) =>
                status === LoadStatus.Error
                    ? throwError(() => new Error('InputFieldPropertiesNgrxService: init failed'))
                    : of(inputFieldPropertiesToModels(this.store.selectSignal(selectInputFieldProperties)())),
            ),
        );
    }

    public getInputFieldProperties(
        fieldName: string,
        screenName: string,
        process: ConfigConstants.PROCESS_INDICATOR,
    ): InputFieldPropertyModel | undefined {
        this.throwIfNotInitialized('getInputFieldProperties');
        const { property, matchCount } = this.store.selectSignal(
            selectInputFieldPropertyMatch({ fieldName, screenName, process }),
        )();
        this.warnOnNonUniqueMatch(fieldName, screenName, matchCount);
        return property ? inputFieldPropertyStateToModel(property) : undefined;
    }

    /**
     * Preserve the legacy pre-initialization guard verbatim (input-field-properties.service.ts:49-53).
     * This is read-path reporting attached to no action, so it cannot become an effect.
     *
     * @param methodName the calling method, for the error message
     */
    private throwIfNotInitialized(methodName: string): void {
        if (!this.isInitialized) {
            throw new Error(
                `InputFieldPropertiesService: ${methodName}: Trying to call InputFieldPropertiesService before initialization!`,
            );
        }
    }

    /**
     * Preserve the legacy warnings verbatim (input-field-properties.service.ts:63,65). This is
     * read-path reporting attached to no action, so it cannot become an effect — and selectors
     * are memoized and pure, so warning inside one would fire only on cache misses.
     *
     * @param fieldName the field that was looked up
     * @param screenName the screen the field is placed on
     * @param matchCount the number of properties the query matched
     */
    private warnOnNonUniqueMatch(fieldName: string, screenName: string, matchCount: number): void {
        if (matchCount === 0) {
            this.log.warn(`FieldName '${fieldName}' and ScreenName '${screenName}' are not a valid identifier!`);
        } else if (matchCount > 1) {
            this.log.warn(`FieldName '${fieldName}' and ScreenName '${screenName}' are not a unique identifier!`);
        }
    }
}
